const tag = `
name
slug
h1
title
description
hidden
id
`

const fetchLotsLastListed = `
  query ($states: [LotStateEnum!]) {
    result: lots(states: $states, page: 1, perPage: 30) {
      id
      name
      images {
        id
        thumbSquare
      }
      adultOnly
      bookingPaymentPercent
      prices {
        dailyRates {
          minDays
          price
        }
        hourlyRates {
          minHours
          price
        }
        hourlyRentAvailable
      }
      suggestedRentPeriodPrices {
        kind
        period
        price
      }
      renterFeeCoefficient
      currency
      user {
        id
      }
    }
  }
`

const fetchLotForEdit = `
  query ($id: ID!) {
    result: lot(id: $id) {
        id
        name
        images {
          id
          thumb
          large
          order
        }
        prices {
          dailyRates {
            minDays
            price
          }
          hourlyRates {
            minHours
            price
          }
          hourlyRentAvailable
        }
        minRentDays
        category {
          id
          name
          slug
          characteristicAssignments {
            characteristic {
              id
              name
              slug
              kind
              items {
                id
                name
                slug
              }
              extra {
                ...on CharacteristicListExtra {
                  multipleSelection
                }
                ...on CharacteristicRangeExtra {
                  unitOfMeasurement
                }
              }
            }
            items {
              id
              name
              slug
            }
          }
        }
        price
        description
        city {
          id
          name
          bbox {
            maxX
            maxY
            minX
            minY
          }
        }
        characteristicAssignments {
          id
          characteristic {
            id
            name
            slug
            kind
            extra {
              ...on CharacteristicListExtra {
                multipleSelection
              }
              ...on CharacteristicRangeExtra {
                unitOfMeasurement
              }
            }
          }
          extra {
            ...on LotCharacteristicAssignmentListExtra {
              items {
                id
                name
                slug
              }
            }
            ... on LotCharacteristicAssignmentRangeExtra {
              value
            }
          }
        }
        equipments {
          name
          count
          id
        }
        address
        location {
          lat
          lng
        }
        currency
        user {
          id
        }
        deleted
        hidden
        doNotRemove
        ownerFeeCoefficient
        secureDeal
        secureDealField
      }
    }
`

const fetchLotOnCreated = `
  query ($id: ID!) {
    result: lot(id: $id) {
      id
      images {
        large
      }
      name
      description
      currency
      prices {
        dailyRates {
          minDays
          price
        }
        hourlyRates {
          minHours
          price
        }
        hourlyRentAvailable
      }
      user {
        publicName
      }
    }
  }
`

const fetchLotWithSiblings = `
  query ($id: ID!) {
    result: lot(id: $id) {
      id
      name
      quantity
      searchRank
      bookingPaymentPercent
      prices {
        dailyRates {
          minDays
          price
        }
        hourlyRates {
          minHours
          price
        }
        hourlyRentAvailable
      }
      suggestedRentPeriodPrices {
			  kind
			  period
			  price
		  }
      cardPrices {
        kind
        period
        price
      }
      hourlyRentPrices {
        kind
        period
        price
      }
      images {
        id
        thumb
        medium
        large
      }
      adultOnly
      address
      location {
        lng
        lat
      }
      currency
      deleted
      hidden
      description
      minRentDays
      price
      deliveryPrice
      renterFeeCoefficient
      secureDeal
      secureDealField
      equipments {
        name
        count
      }
      deposit
      delivery
      createdAt
      category {
        id
        slug
        ancestorIds
        name
        booking
        insurance
        secureDeal
        optionalPrice
        equipmentAndVehicles
        canonicalCategory {
          slug
        }
        ancestors {
          id
          name
          slug
          canonicalCategory {
            slug
          }
        }
      }
      city {
        slug
        name
      }
      isFavorite
      characteristicAssignments {
        id
        characteristic {
          id
          name
          slug
          kind
          extra {
            ...on CharacteristicListExtra {
              multipleSelection
            }
            ...on CharacteristicRangeExtra {
              unitOfMeasurement
            }
          }
        }
        extra {
          ...on LotCharacteristicAssignmentListExtra {
            items {
              id
              name
              slug
            }
          }
          ... on LotCharacteristicAssignmentRangeExtra {
            value
          }
        }
      }
      similarLots {
        id
        images {
          id
          thumbSquare
        }
        category {
          id
          ancestorIds
          insurance
        }
        user {
          id
        }
        name
        adultOnly
        cardPrices {
          kind
          period
          price
        }
        renterFeeCoefficient
        state
        currency
      }
      user {
        id
        publicName
        description
        searchRank
        phonePublic
        phoneProxy
        avatar {
          medium
        }
        avgRating
        rentReviews {
          id
          state
          comment
          createdAt
          rating
          user {
            id
            publicName
            avatar {
              medium
            }
          }
        }
        lots(states: [NEW, MODERATED], page: 1, perPage: 30) {
          collection {
            id
            images {
              id
              thumbSquare
            }
            user {
              id
            }
            name
            adultOnly
            cardPrices {
              kind
              period
              price
            }
            category {
              id
              ancestorIds
              insurance
            }
            renterFeeCoefficient
            state
            currency
          }
        }
        isFavorite
      }
    }
  }
`

const lotCreate = `
  mutation {
    result: lotCreate {
      lot {
        id
        address
        category {
          id
        }
        city {
          id
          name
          bbox {
            maxX
            maxY
            minX
            minY
          }
        }
        images {
          id
          thumb
          order
        }
        location {
          lat
          lng
        }
        prices {
          dailyRates {
            minDays
            price
          }
          hourlyRates {
            minHours
            price
          }
          hourlyRentAvailable
        }
        name
        price
        state
        currency
        secureDeal
        secureDealField
      }
      errors {
        path
        text
      }
    }
  }
`

const lotUpdateName = `
  mutation($lotId:ID!, $name: String!) {
    result: lotUpdateName(lotId: $lotId, name: $name) {
      errors {
        path
        text
      }
      lot {
        name
      }
    }
  }
`

const lotUpdateDescription = `
  mutation($lotId:ID!, $description: String!) {
    result: lotUpdateDescription(lotId: $lotId, description: $description) {
      errors {
        path
        text
      }
      lot {
        description
      }
    }
  }
`

const lotUpdateCategory = `
  mutation($lotId:ID!, $categoryId: ID!) {
    result: lotUpdateCategory(lotId: $lotId, categoryId: $categoryId) {
      errors {
        path
        text
      }
      lot {
        secureDeal
        secureDealField
        category {
          id
          name
          slug
          characteristicAssignments {
            characteristic {
              id
              name
              slug
              kind
              items {
                id
                name
                slug
              }
              extra {
                ...on CharacteristicListExtra {
                  multipleSelection
                }
                ...on CharacteristicRangeExtra {
                  unitOfMeasurement
                }
              }
            }
            items {
              id
              name
              slug
            }
          }
        }
      }
    }
  }
`

const lotUpdatePrices = `
  mutation($lotId:ID!, $prices: LotPricesInput!) {
    result: lotUpdatePrices(lotId: $lotId, prices: $prices) {
      errors {
        path
        text
      }
      lot {
        prices {
          dailyRates {
            minDays
            price
          }
          hourlyRates {
            minHours
            price
          }
          hourlyRentAvailable
        }
      }
    }
  }
`

const lotUpdateAddress = `
  mutation ($lotId: ID!, $address: String!, $location: LocationInput!, $cityId: ID!) {
    result: lotUpdateAddress(lotId: $lotId, address: $address, location: $location, cityId: $cityId) {
      lot {
        address
        location {
          lat
          lng
        }
        city {
          id
          name
          bbox {
            maxX
            maxY
            minX
            minY
          }
        }
      }
      errors {
        path
        text
      }
    }
  }
`

const lotUpdateImageOrder = `
  mutation ($lotId: ID!, $imageIds: [ID!]!) {
    result: lotUpdateImageOrder(lotId: $lotId, imageIds: $imageIds) {
    lot {
      images {
          id
          thumb
          large
          order
        }
      }
      errors {
        path
        text
      }
    }
  }
`

const lotUpdatePrice = `
  mutation ($lotId: ID!, $price: Float!) {
    result: lotUpdatePrice(lotId: $lotId, price: $price) {
      lot {
        price
      }
      errors {
        path
        text
      }
    }
  }
`

const lotAddImage = `
  mutation ($lotId: ID!, $image: ImageInput!) {
    result: lotAddImage(lotId: $lotId, image: $image) {
      image {
        id
      }
      errors {
        path
        text
      }
    }
  }
`

const lotRemoveImage = `
  mutation ($imageId: ID!) {
    result: lotRemoveImage(imageId: $imageId) {
      errors {
        path
        text
      }
    }
  }
`

const lotPublish = `
  mutation ($lotId: ID!) {
    result: lotPublish(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        state
      }
    }
  }
`

const lotAddCharacteristicItemList = `
  mutation ($lotId: ID!, $characteristicItemId: ID!) {
    result: lotAddCharacteristicItemList(lotId: $lotId, characteristicItemId: $characteristicItemId) {
      errors {
        path
        text
      }
      lot {
        characteristicAssignments {
          id
          characteristic {
            id
            name
            slug
            kind
            extra {
              ...on CharacteristicListExtra {
                multipleSelection
              }
              ...on CharacteristicRangeExtra {
                unitOfMeasurement
              }
            }
          }
          extra {
            ...on LotCharacteristicAssignmentListExtra {
              items {
                id
                name
                slug
              }
            }
            ... on LotCharacteristicAssignmentRangeExtra {
              value
            }
          }
        }
      }
    }
  }
`

const lotRemoveCharacteristicItemList = `
  mutation ($lotId: ID!, $characteristicItemId: ID!) {
    result: lotRemoveCharacteristicItemList(lotId: $lotId, characteristicItemId: $characteristicItemId) {
      errors {
        path
        text
      }
      lot {
        characteristicAssignments {
          id
          characteristic {
            id
            name
            slug
            kind
            extra {
              ...on CharacteristicListExtra {
                multipleSelection
              }
              ...on CharacteristicRangeExtra {
                unitOfMeasurement
              }
            }
          }
          extra {
            ...on LotCharacteristicAssignmentListExtra {
              items {
                id
                name
                slug
              }
            }
            ... on LotCharacteristicAssignmentRangeExtra {
              value
            }
          }
        }
      }
    }
  }
`

const lotAddCharacteristicRange = `
  mutation ($lotId: ID!, $characteristicId: ID!, $value: Float!) {
    result: lotAddCharacteristicRange(lotId: $lotId, characteristicId: $characteristicId, value: $value) {
      errors {
        path
        text
      }
      lot {
        characteristicAssignments {
          id
          characteristic {
            id
            name
            slug
            kind
          }
          extra {
            ...on LotCharacteristicAssignmentListExtra {
              items {
                id
                name
                slug
              }
            }
            ... on LotCharacteristicAssignmentRangeExtra {
              value
            }
          }
        }
      }
    }
  }
`

const lotRemoveCharacteristicRange = `
  mutation ($lotId: ID!, $characteristicId: ID!) {
    result: lotRemoveCharacteristicRange(lotId: $lotId, characteristicId: $characteristicId) {
      errors {
        path
        text
      }
      lot {
        characteristicAssignments {
          id
          characteristic {
            id
            name
            slug
            kind
          }
          extra {
            ...on LotCharacteristicAssignmentListExtra {
              items {
                id
                name
                slug
              }
            }
            ... on LotCharacteristicAssignmentRangeExtra {
              value
            }
          }
        }
      }
    }
  }
`

const autocompleteEquipmentType = `
  query ($q: String!, $categoryId: ID!) {
    result: autocompleteEquipmentType(q: $q, categoryId: $categoryId) {
      token
    }
  }
`

const lotAddEquipment = `
  mutation ($lotId: ID!, $name: String!, $count: Int!) {
    result: lotAddEquipment(lotId: $lotId, name: $name, count: $count) {
      errors {
        path
        text
      }
      lot {
        equipments {
          name
          count
          id
        }
      }
    }
  }
`

const lotRemoveEquipment = `
  mutation ($lotId: ID!, $equipmentId: ID!) {
    result: lotRemoveEquipment(lotId: $lotId, equipmentId: $equipmentId) {
      errors {
        path
        text
      }
      lot {
        equipments {
          name
          count
          id
        }
      }
    }
  }
`

const lotDelete = `
  mutation ($lotId: ID!) {
    result: lotDelete(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        deleted
      }
    }
  }
`

const lotApprove = `
  mutation ($lotId: ID!) {
    result: lotApprove(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        state
      }
    }
  }
`

const lotBlock = `
  mutation ($lotId: ID!) {
    result: lotBlock(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        state
      }
    }
  }
`

const lotHide = `
  mutation ($lotId: ID!) {
    result: lotHide(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        hidden
      }
    }
  }
`

const lotShow = `
  mutation ($lotId: ID!) {
    result: lotShow(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        hidden
      }
    }
  }
`

const lotUpdateDoNotRemove = `
  mutation ($lotId: ID!) {
    result: lotUpdateDoNotRemove(lotId: $lotId) {
      errors {
        path
        text
      }
      lot {
        doNotRemove
      }
    }
  }
`

const lotUpdateSecureDeal = `
  mutation($lotId: ID!, $secureDeal: Boolean!) {
    result: lotUpdateSecureDeal(lotId: $lotId, secureDeal: $secureDeal) {
      errors {
        path
        text
      }
      lot {
        secureDeal
        secureDealField
      }
    }
  }
`

const calculatePrice = `
  query ($lotId: ID!, $dateFrom: ISO8601DateTime!, $dateTo: ISO8601DateTime!, $deliveryFlag: Boolean, $insuranceFlag: Boolean, $rentHours: Int) {
    result: calculatePrice(lotId: $lotId, dateFrom: $dateFrom, dateTo: $dateTo, deliveryFlag: $deliveryFlag, insuranceFlag: $insuranceFlag, rentHours: $rentHours) {
      delivery
      deliveryPrice
      deposit
      ownerServiceFee
      rent
      renterServiceFee
      insurance
      insurancePrice
      totalPrice
      totalReward
      totalRent
    }
  }
`

const fetchlotsForAdmin = `
  query ($states: [LotStateEnum!], $page: Int) {
    result: lots(states: $states, page: $page, perPage: 30) {
      collection {
        id
        quantity
        name
        city {
          name
        }
        deposit
        state
        category {
          id
          ancestorIds
        }
        user {
          id
          nameForAdmin
          searchRank
        }
        bookingPaymentPercent
        prices {
          dailyRates {
            minDays
            price
          }
          hourlyRates {
            minHours
            price
          }
          hourlyRentAvailable
        }
        currency
        hidden
        deleted
        doNotRemove
        searchRank
        partner
        tags {
          ${tag}
        }
        ownerFeeCoefficient
      }
      metadata {
        currentPage
        currentPerPage
        nextPage
        prevPage
        totalCount
        totalPages
      }
    }
  }
`

const fetchlotForAdminByLotId = `
  query ($id: ID!) {
    result: lot(id: $id) {
      id
      quantity
      name
      city {
        name
      }
      deposit
      state
      category {
        id
        ancestorIds
      }
      user {
        id
        nameForAdmin
        searchRank
      }
      bookingPaymentPercent
      prices {
        dailyRates {
          minDays
          price
        }
        hourlyRates {
          minHours
          price
        }
        hourlyRentAvailable
      }
      currency
      deleted
      hidden
      doNotRemove
      searchRank
      partner
      tags {
        ${tag}
      }
      ownerFeeCoefficient
    }
  }
`

const fetchlotsForAdminByUserId = `
  query ($id: ID!, $page: Int, $states: [LotStateEnum!]) {
    result: user(id: $id) {
      lots (page: $page, states: $states, perPage: 30) {
        collection {
          id
          quantity
          name
          city {
            name
          }
          deposit
          state
          category {
            id
            ancestorIds
          }
          user {
            id
            nameForAdmin
            searchRank
          }
          bookingPaymentPercent
          prices {
            dailyRates {
              minDays
              price
            }
            hourlyRates {
              minHours
              price
            }
            hourlyRentAvailable
          }
          hidden
          deleted
          doNotRemove
          currency
          searchRank
          partner
          tags {
            ${tag}
          }
          ownerFeeCoefficient
        }
        metadata {
          currentPage
          currentPerPage
          nextPage
          prevPage
          totalCount
          totalPages
        }
      }
    }
  }
`

const fetchLotsByTagsId = `
  query ($tagId: ID!, $page: Int, $states: [LotStateEnum!]) {
    result: tag(tagId: $tagId) {
      lots(page: $page, states: $states) {
        collection {
          id
          quantity
          name
          city {
            name
          }
          deposit
          state
          category {
            id
            ancestorIds
          }
          user {
            id
            nameForAdmin
            searchRank
          }
          bookingPaymentPercent
          prices {
            dailyRates {
              minDays
              price
            }
            hourlyRates {
              minHours
              price
            }
            hourlyRentAvailable
          }
          hidden
          deleted
          doNotRemove
          currency
          searchRank
          tags {
            ${tag}
          }
          ownerFeeCoefficient
        }
        metadata {
          currentPage
          currentPerPage
          nextPage
          prevPage
          totalCount
          totalPages
        }
      }
    }
  }
`

const fetchLotsByTagsIdForPromo = `
  query ($tagId: ID!, $states: [LotStateEnum!], $page: Int, $perPage: Int) {
    result: tag(tagId: $tagId) {
      h1
      id
      title
      description
      name
      slug
      lots(states: $states, page: $page, perPage: $perPage) {
        collection {
          renterFeeCoefficient
          id
          name
          user {
            id
            lotsCount
            publicName
            avgRating
          }
          bookingPaymentPercent
          cardPrices {
            kind
            period
            price
          }
          currency
          tags {
            ${tag}
          }
          images {
            id
            medium
            thumbSquare
          }
          adultOnly
        }
        metadata {
          currentPage
          currentPerPage
          nextPage
          prevPage
          totalCount
          totalPages
        }
      }
    }
  }
`

export default {
  fetchLotOnCreated,
  fetchLotForEdit,
  fetchLotWithSiblings,
  fetchLotsLastListed,
  lotAddCharacteristicItemList,
  lotRemoveCharacteristicItemList,
  lotCreate,
  lotUpdateName,
  lotUpdateDescription,
  lotUpdateCategory,
  lotUpdatePrice,
  lotUpdatePrices,
  lotUpdateAddress,
  lotUpdateImageOrder,
  lotAddImage,
  lotRemoveImage,
  lotPublish,
  autocompleteEquipmentType,
  lotAddEquipment,
  lotRemoveEquipment,
  calculatePrice,
  lotDelete,
  lotApprove,
  lotBlock,
  lotHide,
  lotShow,
  lotUpdateDoNotRemove,
  lotUpdateSecureDeal,
  fetchlotsForAdmin,
  fetchlotForAdminByLotId,
  fetchlotsForAdminByUserId,
  fetchLotsByTagsId,
  fetchLotsByTagsIdForPromo,
  lotAddCharacteristicRange,
  lotRemoveCharacteristicRange,
}
